import { t } from '@exam-prep/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@exam-prep/ui';
import type { Hub, MockSummary } from '../../../lib/api-types';
import { libraryGroups } from '../model/library-groups';

export function MockRow({ m, onOpen }: { m: MockSummary; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="tap-row justify-between">
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{m.title}</span>
        <span className="block text-xs text-muted-foreground">
          {t('miniapp.mock.meta', { questions: m.questions, minutes: m.durationMin ?? 0 })}
        </span>
      </span>
      <span className="inline-flex h-9 shrink-0 items-center rounded-full bg-[image:var(--brand-amber-gradient)] px-4 text-xs font-bold text-[var(--brand-amber-ink)]">
        {t('miniapp.mock.start')} →
      </span>
    </button>
  );
}

/** The learner's finished mocks, newest first. */
export function MyResults({
  results,
  onResult,
}: {
  results: Hub['results'];
  onResult: (attemptId: string) => void;
}) {
  const done = results.filter((res) => res.status !== 'in_progress');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('miniapp.hub.results')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {done.length ? (
          done.map((res) => (
            <button
              key={res.id}
              type="button"
              onClick={() => onResult(res.id)}
              className="tap-row justify-between text-sm"
            >
              <span className="min-w-0 font-medium">{res.title}</span>
              <span className="shrink-0 rounded-full border border-input px-3 py-1 font-display text-sm font-semibold tabular-nums">
                {res.score === null ? '–' : `${String(res.score)} / ${String(res.maxScore ?? 0)}`}
              </span>
            </button>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">{t('miniapp.hub.results_empty')}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** The Mock library, under Tier 1 / Tier 2 headings when it holds more than one stage. */
export function MockLibrary({
  library,
  onOpen,
}: {
  library: Hub['library'];
  onOpen: (mockId: string) => void;
}) {
  if (!library.length)
    return <p className="text-sm text-muted-foreground">{t('miniapp.hub.library_empty')}</p>;
  return (
    <>
      {libraryGroups(library).map((group) => (
        <section key={group.stageKey} className="space-y-2" aria-label={group.heading ?? undefined}>
          {group.heading ? (
            <h3 className="pt-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {group.heading}
            </h3>
          ) : null}
          {group.mocks.map((mock) => (
            <MockRow key={mock.id} m={mock} onOpen={() => onOpen(mock.id)} />
          ))}
        </section>
      ))}
    </>
  );
}
